import { useCatalogContext } from '@apps/registry/common/components/Catalog/state/Catalog';
import { useHistory, useLocation } from '@shared/core';
import { createContext } from '@shared/utils/createContext';
import { useDisclosure } from '@withjoy/joykit';
import React, { useCallback, useEffect, useState } from 'react';
import { catalogCacheApi } from '../../Catalog.cache';
import { useFormattedRefinementFacetHref } from '../../hooks/useFormattedRefinementFacetHref/useFormattedRefinementFacetHref';
import { useDeriveTelemetryPage } from '../../utils/deriveTelemetryPage';
import { useSearchTermQueryParam } from '../../utils/queryParams';
import { useUrlSyncedFilters } from './useUrlSyncedFilters/useUrlSyncedFilters';
import { useCatalogLoggingContext } from '../../Catalog.logging';
import { useFeatureValue } from '@shared/core/featureFlags';
import { useIsMobileOrTablet } from '@shared/utils/media/useMediaScreens';

type RefinementsPanelContext = {
  // State
  isOpen: boolean;
  // Operations
  openPanel: (e: React.MouseEvent<HTMLElement>) => void;
  closePanel: (e?: React.MouseEvent<HTMLElement>) => void;
  checkIsFilterGroupViewExpanded: (groupCode: string) => boolean;
  getActiveFilterCountByGroupCode: (groupCode: string) => number;
  getTotalActiveFilterCount: () => number;
  toggleSearchFilter: (attributeCompositeCode: string) => void;
  toggleGroupFullyExpand: (attributeCompositeCode: string) => void;
  clearAllSearchFilters: () => void;
};

const [Provider, useRefinementsPanelProvider] = createContext<RefinementsPanelContext>({ name: 'RefinementsPanelProvider' });

interface RefinementsPanelProviderProps
  extends Readonly<{
    children: React.ReactNode;
  }> {}

const RefinementsPanelProvider = (props: RefinementsPanelProviderProps) => {
  const { children } = props;
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const isMobileOrTablet = useIsMobileOrTablet();
  const registryCatalogFiltersOpenDefaultEnabled = useFeatureValue('registryCatalogFiltersOpenDefaultEnabled', { skip: isMobileOrTablet }).value === 'treatment';
  const showFiltersAsDefault: boolean = params.get('showFilters') === 'true';
  const { isOpen, onOpen, onClose } = useDisclosure({ defaultIsOpen: showFiltersAsDefault });

  useEffect(() => {
    if (registryCatalogFiltersOpenDefaultEnabled) {
      onOpen();
    }
  }, [registryCatalogFiltersOpenDefaultEnabled, onOpen]);

  const history = useHistory();
  useEffect(() => {
    const unsub = history.listen((loc, action) => {
      if (action === 'PUSH') {
        const params = new URLSearchParams(loc.search);
        const shouldShowFilters = params.get('showFilters') === 'true';
        params.delete('showFilters');

        if (shouldShowFilters) {
          onOpen();
        }

        history.replace(`${loc.pathname}?${params.toString()}`);
      }
    });

    return () => {
      unsub();
    };
  }, [onOpen, history]);

  const { selectedCategory } = useCatalogContext();
  const searchTerm = useSearchTermQueryParam();
  const { trackRefinementPanelToggleClicked } = useCatalogLoggingContext();

  const { resetFiltersHref } = useFormattedRefinementFacetHref();
  const [isGroupFullyExpanded, setIsGroupFullyExpanded] = useState<Record<string, boolean>>({});

  const deriveTelemetryPage = useDeriveTelemetryPage();
  const telemetryPage = deriveTelemetryPage({ view: 'productGrid', selectedCategory, product: null });

  useUrlSyncedFilters();

  const handleClearAllSearchFilters = useCallback(() => {
    catalogCacheApi.clearAllSearchFilters();
    history.push(resetFiltersHref());
  }, [resetFiltersHref, history]);

  useEffect(() => {
    setIsGroupFullyExpanded({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory, searchTerm]);

  const checkIsFilterGroupViewExpanded = useCallback(
    (groupCode: string) => {
      return !!isGroupFullyExpanded[groupCode];
    },
    [isGroupFullyExpanded]
  );

  const handleToggleGroupFullyExpand = useCallback(
    (code: string) => {
      setIsGroupFullyExpanded(prev => ({
        ...prev,
        [code]: !isGroupFullyExpanded[code]
      }));
    },
    [isGroupFullyExpanded]
  );

  const openPanel = useCallback<React.MouseEventHandler<HTMLElement>>(
    e => {
      onOpen();
      const method = e.currentTarget.dataset?.togglemethod;
      if (method) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        trackRefinementPanelToggleClicked({ label: 'show', method: method as any, page: telemetryPage });
      }
    },
    [onOpen, trackRefinementPanelToggleClicked, telemetryPage]
  );
  const closePanel = useCallback(
    (e?: React.MouseEvent<HTMLElement>) => {
      onClose();
      const method = e?.currentTarget?.dataset?.togglemethod;
      if (method) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        trackRefinementPanelToggleClicked({ label: 'hide', method: method as any, page: telemetryPage });
      }
    },
    [onClose, trackRefinementPanelToggleClicked, telemetryPage]
  );

  return (
    <Provider
      value={{
        isOpen,
        checkIsFilterGroupViewExpanded,
        closePanel,
        openPanel,
        getActiveFilterCountByGroupCode: catalogCacheApi.getActiveFilterCountByGroupCode,
        getTotalActiveFilterCount: catalogCacheApi.getTotalActiveFilterCount,
        toggleSearchFilter: catalogCacheApi.toggleSearchFilter,
        toggleGroupFullyExpand: handleToggleGroupFullyExpand,
        clearAllSearchFilters: handleClearAllSearchFilters
      }}
    >
      {children}
    </Provider>
  );
};

RefinementsPanelProvider.displayName = 'RefinementsPanelProvider';

export { RefinementsPanelProvider, useRefinementsPanelProvider };
